// src/pages/Auth/ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { forgotPassword, resetPassword } from '../../api/authService';
import Button from '../../components/ui/Button';
import './Auth.css';

/**
 * Réinitialisation de mot de passe en 2 phases :
 *  1. Sans token → formulaire « nom d'utilisateur ou email » → génère le lien
 *     (en dev le lien est affiché directement, faute de SMTP branché)
 *  2. Avec ?token=... → formulaire nouveau mot de passe
 */
const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || ''; // dérivé : suit l'URL même sans remontage
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetUrl, setResetUrl] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Phase 1 : demande du lien
  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const data = await forgotPassword(usernameOrEmail);
      if (data.resetUrl) setResetUrl(data.resetUrl);
      setMessage(data.message || 'Demande enregistrée.');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la demande');
    } finally {
      setLoading(false);
    }
  };

  // Phase 2 : nouveau mot de passe
  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      const data = await resetPassword(token, newPassword);
      setMessage(data.message || 'Mot de passe réinitialisé');
      setTimeout(() => navigate('/auth/login'), 1800);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{token ? 'Nouveau mot de passe' : 'Mot de passe oublié'}</h1>
          <p>
            {token
              ? 'Choisissez un nouveau mot de passe pour votre compte'
              : 'Indiquez votre nom d\'utilisateur ou votre email'}
          </p>
        </div>

        {error && <div className="auth-error">{error}</div>}
        {message && <div className="auth-success">{message}</div>}

        {token ? (
          <form className="auth-form" onSubmit={handleReset}>
            <div className="form-group">
              <label htmlFor="newPassword">Nouveau mot de passe</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength="6"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmer</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength="6"
              />
            </div>
            <Button type="primary" disabled={loading} fullWidth>
              {loading ? 'Enregistrement…' : 'Réinitialiser le mot de passe'}
            </Button>
          </form>
        ) : (
          <form className="auth-form" onSubmit={handleRequest}>
            <div className="form-group">
              <label htmlFor="usernameOrEmail">Nom d'utilisateur ou email</label>
              <input
                type="text"
                id="usernameOrEmail"
                name="usernameOrEmail"
                value={usernameOrEmail}
                onChange={(e) => setUsernameOrEmail(e.target.value)}
                required
              />
            </div>
            <Button type="primary" disabled={loading} fullWidth>
              {loading ? 'Envoi…' : 'Recevoir le lien de réinitialisation'}
            </Button>
          </form>
        )}

        {/* En dev (pas de SMTP) : le lien est affiché ici au lieu d'être envoyé par mail */}
        {resetUrl && (
          <div className="reset-link-box" style={{ marginTop: '1rem', wordBreak: 'break-all' }}>
            <p style={{ fontSize: '0.85rem', opacity: 0.8 }}>
              Mode dev (pas d'email configuré) — votre lien :
            </p>
            <Link to={resetUrl}>{resetUrl}</Link>
          </div>
        )}

        <div className="auth-links">
          <p>
            <Link to="/auth/login">Retour à la connexion</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
